import clsx from 'clsx'

import { Button } from 'src/components/Button/Button'
import { Text } from 'src/components/Text/Text'
import { getAvailableOptions, findVariantByOption, isOptionActive } from 'src/functions/product/productOptions'
import { getMetafield } from 'src/functions/util/getMetafield'

import styles from './VariantOptions.module.scss'

import type { HTMLAttributes } from 'react'
import type { Product, ProductOption, ProductOptionValue, ProductVariant } from 'src/data/graphql-generated'

interface VariantOptionsProps extends HTMLAttributes<HTMLDivElement> {
  product: Product
  variant: ProductVariant
  setVariantSku: (sku: ProductVariant['sku']) => void
}

export const VariantOptions = ({ product, variant, setVariantSku, className }: VariantOptionsProps) => {
  const {
    variants: { nodes: variants },
  } = product

  const variantsWithoutArchived = variants.filter((v) => !getMetafield(v.metafields || [], 'product', 'archived'))
  if (variantsWithoutArchived.length === 1) return null

  const availableOptions = getAvailableOptions(product, variant)

  const selectOption = (optionName: ProductOption['name'], optionValue: ProductOptionValue): void => {
    const newVariant = findVariantByOption(product, variant, optionName, optionValue)
    setVariantSku(newVariant.sku)
  }

  return (
    <div className={clsx(styles.container, className)}>
      {availableOptions.map(({ id, name, optionValues }) => (
        <div key={id} className={styles.variantGroup}>
          <Text
            variant="span"
            bold
            className={clsx(styles.variantGroupLabel, 'notranslate')}
            data-testid="option-group-label"
            translate="no"
          >
            {name}
          </Text>

          <div className={styles.variantOptions}>
            {optionValues.map((value) => (
              <Button
                key={`${value.name}-${id}`}
                variant={isOptionActive(variant, name, value) ? 'primary' : 'secondary'}
                onClick={() => selectOption(name, value)}
                translate="no"
                className="notranslate"
                data-testid="option"
                data-analytics-id="change-variant"
              >
                {value.name}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
